import { AnimatePresence, motion } from 'framer-motion';
import React, { useState } from 'react';
import { Spacer } from 'src/common';
import Button from 'src/components/form/Button';
import MaterialInput from 'src/components/form/MaterialInput';
import { EventKind, ObjectKind, useUpsertEventMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { showSuccessToast, toastMessages } from 'src/components/Toast';

interface AddCommentProps {
  eventData: { objectType: ObjectKind; objectId: string };
  onCommentAdded: () => void;
}

export const AddComment: React.FC<AddCommentProps> = ({ eventData, onCommentAdded }) => {
  const [comment, setComment] = useState('');
  const { workspaceId } = useAuth();

  const [upsertEvent, { loading }] = useUpsertEventMutation({
    onCompleted: () => {
      setComment('');
      onCommentAdded();
      showSuccessToast(toastMessages.comment.added);
    },
  });

  const handlePost = () => {
    const trimmed = comment.trim();
    if (!trimmed) return;

    upsertEvent({
      variables: {
        workspaceId,
        eventData: [
          {
            eventType: EventKind.Comment,
            notes: trimmed,
            ...eventData,
          },
        ],
      },
    });
  };

  return (
    <div>
      <MaterialInput
        placeholder="Write a comment"
        multiline
        minRows={1}
        maxRows={3}
        fullWidth
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        error={comment.length > 1000}
        helperText={comment.length > 1000 ? 'Cannot exceed 1000 characters' : undefined}
        data-cy="comment-input"
      />
      <AnimatePresence initial={false}>
        {comment.trim() ? (
          <motion.div
            initial={{ opacity: 0, height: 0, y: 10 }}
            animate={{ opacity: 1, height: 'auto', y: 0 }}
            exit={{ opacity: 0, height: 0, y: 10 }}
            transition={{ duration: 0.1 }}
            style={{ overflow: 'hidden' }}
          >
            <Spacer $px={10} />
            <Button primary $small onClick={handlePost} disabled={!comment.trim() || comment.length > 1000 || loading} loading={loading} data-cy="submit-btn">
              POST
            </Button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};
